import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function PairMaster() {
  const [pairs, setPairs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ up_train: "", down_train: "", name: "" });
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data } = await api.get("/pairs", { params: { include_retired: true } });
    setPairs(data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.up_train || !form.name) { toast.error("Up train + name required"); return; }
    try {
      await api.post("/pairs", form);
      toast.success("Pair created");
      setShowAdd(false);
      setForm({ up_train: "", down_train: "", name: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveEdit = async () => {
    await api.patch(`/pairs/${encodeURIComponent(editing.pair_code)}`, {
      name: form.name,
    });
    toast.success("Updated");
    setEditing(null);
    load();
  };

  const del = async (pc) => {
    if (!window.confirm(`Delete pair ${pc}?`)) return;
    await api.delete(`/pairs/${encodeURIComponent(pc)}`);
    toast.success("Deleted");
    load();
  };

  const retire = async (p) => {
    await api.patch(`/pairs/${encodeURIComponent(p.pair_code)}`, { retired: !p.retired });
    load();
  };

  const filtered = pairs.filter((p) => !search || `${p.pair_code} ${p.name}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Pair Master</h1>
          <p className="text-sm text-slate-500 mt-1">{pairs.length} pairs · {pairs.filter(p => p.retired).length} retired</p>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="pair-search-input"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 w-56"
          />
          <Button data-testid="add-pair-btn" onClick={() => setShowAdd(true)} className="bg-blue-600 hover:bg-blue-500 text-white h-9">
            <Plus className="h-4 w-4 mr-1" /> Add Pair
          </Button>
        </div>
      </div>

      <Card className="border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white text-[11px] uppercase tracking-wider">
              <th className="text-left px-4 py-2.5">Pair Code</th>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Up</th>
              <th className="text-left px-4 py-2.5">Down</th>
              <th className="text-center px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 text-sm">No pairs — add one to start.</td></tr>}
            {filtered.map((p) => (
              <tr key={p.pair_code} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono font-semibold text-slate-900">{p.pair_code}</td>
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.up_train}</td>
                <td className="px-4 py-2 font-mono text-xs">{p.down_train || "—"}</td>
                <td className="px-4 py-2 text-center">
                  <button
                    data-testid={`retire-toggle-${p.pair_code}`}
                    onClick={() => retire(p)}
                    className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${p.retired ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}
                  >
                    {p.retired ? "Retired" : "Active"}
                  </button>
                </td>
                <td className="px-4 py-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(p); setForm({ up_train: p.up_train, down_train: p.down_train, name: p.name }); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button data-testid={`delete-pair-${p.pair_code}`} size="sm" variant="ghost" onClick={() => del(p.pair_code)}>
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Add train pair</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="new-up-train" placeholder="Up train (e.g. 12625)" value={form.up_train} onChange={(e) => setForm({ ...form, up_train: e.target.value })} className="font-mono" />
            <Input data-testid="new-down-train" placeholder="Down train (e.g. 12626)" value={form.down_train} onChange={(e) => setForm({ ...form, down_train: e.target.value })} className="font-mono" />
            <Input data-testid="new-pair-name" placeholder="Pair name (e.g. Kerala Express)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button data-testid="save-new-pair" className="bg-blue-600 hover:bg-blue-500 text-white" onClick={create}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Edit pair</DialogTitle></DialogHeader>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-500 text-white" onClick={saveEdit}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
